import {getLocale} from '@epic-core/common';
import {isAxiosError} from '@epic-mw/error-types';
import axios from 'axios';
import {
  Alert,
  Box,
  Flex,
  FlexItem,
  Form,
  Inline,
  Modal,
  ModalProps,
  Progress,
  Stack,
  Text,
  TextField,
  UtilityButton,
  VisuallyHidden,
} from 'epic-fortnite-react';
import {getOrigin} from 'epic-fortnite-shared-values';
import {Formik, Field as FormikField} from 'formik';
import queryString from 'query-string';
import React from 'react';
import styled from 'styled-components';
import {v4 as uuidv4} from 'uuid';
import * as yup from 'yup';

import {Island} from '../../../server/handlers/creative-form/creative.types';
import {SubmitIslandPageData} from '../types';
import {useUploadProgress} from '../useUploadProgress.hooks';

import {FormikFileUpload} from './FormikFileUpload';
import LinkParagraph from './LinkParagraph';

const StyledIslandFormModal = styled(Modal)`
  --modal-margin: 0px;

  align-self: flex-start;
  background-color: #fff;
  display: flex;
  filter: drop-shadow(0px 0px 16px rgba(0, 0, 0, 0.15));
  flex-direction: column;
  margin: var(--modal-margin) auto;
  max-height: calc(100% - (var(--modal-margin) * 2));
  max-width: 800px;
  overflow-x: hidden;
  overflow-y: auto;
  position: relative;
  width: 100%;

  @media (min-width: 600px) {
    --modal-margin: 48px;
  }
`;

const StyledIslandFormModalCloseButton = styled.button`
  appearance: none;
  background-color: rgba(0, 0, 0, 0.4);
  border: 0;
  height: 32px;
  width: 32px;
  text-align: center;
  z-index: 99;

  &.focus-visible {
    box-shadow: inset 0px 0px 0px 4px #000;
  }
`;

const StyledTag = styled.div`
  padding: 8px;
  background-color: #e5e5e5;
`;

interface Props extends Omit<ModalProps, 'ariaLabel'> {
  island: Island | undefined;
  hasExceededPendingLimit: boolean;
  email: string;
  translations: SubmitIslandPageData;
  submissionType?: string;
}

interface InitialValues {
  islandCode: string;
  islandType?: string;
  islandVersion: number;
  islandTitle: string;
  islandDescription: string;
  maxPlayerCount: number;
  tags: string[];
  gameGenre?: string;
  image?: string;
  trailer?: string;
  acceptContentGuidelines?: boolean;
}

const initialValues = ({
  islandCode,
  islandType,
  islandVersion,
  islandTitle,
  islandDescription,
  maxPlayerCount,
  tags,
}: InitialValues) => ({
  islandCode,
  islandType,
  islandVersion,
  islandTitle,
  islandDescription,
  maxPlayerCount,
  gameGenre: tags?.join(';'),
  image: undefined,
  trailer: undefined,
  acceptContentGuidelines: false,
});

const uploadFile = async (
  submissionID: string,
  file: File,
  onUploadProgress: (ev: ProgressEvent) => void,
  submissionType?: string
) => {
  let signedS3Url: string;
  try {
    const response = await axios.post(
      `${getOrigin()}/${getLocale()}/api/creative-submission-form/upload-image?${queryString.stringify(
        {
          submissionIdentifier: submissionID,
          submissionType,
          type: file.type,
        }
      )}`
    );
    signedS3Url = response.data.url;
  } catch (err) {
    console.log('failed to initialize upload');
    throw err;
  }

  return await axios.put(signedS3Url, file, {
    onUploadProgress,
    headers: {
      'Content-Type': file.type,
    },
  });
};

export const IslandFormModal = ({
  isOpen,
  onDismiss,
  island,
  hasExceededPendingLimit,
  email,
  translations,
  submissionType,
}: Props) => {
  const submissionIdentifier = React.useRef(uuidv4());
  // Cycle submissionIdentifier when modal open/closes
  React.useEffect(() => {
    submissionIdentifier.current = uuidv4();
  }, [isOpen]);

  const {trackFileProgress, handleUploadProgress, progress} = useUploadProgress();
  const hasBeenModerated =
    island?.moderationStatus === 'APPROVED' || island?.moderationStatus === 'DENIED';
  const isPendingModeration = island?.moderationStatus === 'PENDING';
  const isSubmissionAllowed =
    !hasExceededPendingLimit && !island?.isOutdated && !hasBeenModerated && !isPendingModeration;

  const handleSubmit =
    (submissionIdentifier: string) =>
    async ({acceptContentGuidelines, trailer, image, ...rest}, {setStatus}) => {
      if (image) {
        trackFileProgress(image);
      }
      if (trailer) {
        trackFileProgress(trailer);
      }

      if (image) {
        try {
          await uploadFile(submissionIdentifier, image, handleUploadProgress(image.name));
        } catch (err) {
          setStatus({message: translations?.submissionForm?.mediaUpload?.imageUploadFail});
          return;
        }
      }

      if (trailer) {
        try {
          await uploadFile(submissionIdentifier, trailer, handleUploadProgress(trailer.name));
        } catch (err) {
          setStatus({message: translations?.submissionForm?.mediaUpload?.videoUploadFail});
          return;
        }
      }

      try {
        await axios.post(`${getOrigin()}/${getLocale()}/api/creative-submission-form/submit`, {
          image: Boolean(image),
          trailer: Boolean(trailer),
          payload: {
            submissionIdentifier,
            submissionType,
            ...rest,
          },
        });
      } catch (err) {
        if (isAxiosError(err) && err.response?.status === 400) {
          if (err.response.data.errors) {
            const {validationMessages} = translations.submissionForm;
            const errorMessageMap = {
              VIDEO_CODEC: validationMessages.videoRequirementsCodecMessage,
              AUDIO_CODEC: validationMessages.videoRequirementsAudioMessage,
              ASPECT_RATIO: validationMessages.videoRequirementsRatioMessage,
              RESOLUTION: validationMessages.videoRequirementsResolutionMessage,
            };
            const messages = err.response.data.errors.map((error) => errorMessageMap[error]);
            return setStatus({
              message: (
                <>
                  {validationMessages.videoRequirementsMessage}{' '}
                  <ul>
                    {messages.map((message, index) => (
                      <li key={index}>{message}</li>
                    ))}
                  </ul>
                </>
              ),
            });
          }
        }
        setStatus({message: translations?.submissionForm?.formSubmissionFail});
        return;
      }
      if (onDismiss) {
        onDismiss();
      }
    };

  const {fieldRequiredMessage} = translations.submissionForm.validationMessages;

  return (
    <StyledIslandFormModal
      ariaLabel={island?.title ?? ''}
      isOpen={isOpen}
      closeButton={null}
      onDismiss={onDismiss}
      overlayColor="dark">
      {island ? (
        <Formik
          validationSchema={yup.object().shape({
            image: island.image ? yup.mixed() : yup.mixed().required(fieldRequiredMessage),
            trailer: yup.mixed(),
            acceptContentGuidelines: yup
              .boolean()
              .oneOf([true], fieldRequiredMessage)
              .required(fieldRequiredMessage),
          })}
          initialValues={initialValues({
            islandCode: island.code,
            islandType: island.islandType,
            islandVersion: island.version,
            islandTitle: island.title,
            islandDescription: island.description,
            maxPlayerCount: island.maximumNumberOfPlayers,
            tags: island.tags,
          })}
          onSubmit={handleSubmit(submissionIdentifier.current)}>
          {({
            handleSubmit,
            isSubmitting,
            isValid,
            handleChange,
            handleBlur,
            values,
            errors,
            setFieldValue,
            dirty,
            touched,
            submitForm,
            status,
          }) => (
            <Flex direction="column" scrollable={true}>
              <FlexItem>
                <Box backgroundColor="#0B85EC" size="lg">
                  <Inline justify="space-between">
                    <Text color="#fff" size="2xl" transform="uppercase">
                      {translations?.submissionForm?.title}
                    </Text>
                    <StyledIslandFormModalCloseButton onClick={onDismiss}>
                      <VisuallyHidden>
                        {translations?.submissionForm?.closeModalString}
                      </VisuallyHidden>
                      <Text aria-hidden="true" color="#fff" size="lg">
                        X
                      </Text>
                    </StyledIslandFormModalCloseButton>
                  </Inline>
                </Box>
              </FlexItem>
              <FlexItem flex={true} scrollable={true}>
                <Box backgroundColor="#E5E5E5" size="2xl">
                  <Stack gap="xl">
                    <Stack gap="med">
                      <Text size="lg" transform="uppercase">
                        {island.title}
                      </Text>
                      <Text color="#5E5E5E" size="med" transform="uppercase">
                        {island.code}
                      </Text>
                    </Stack>
                    <Stack gap="lg">
                      <Inline gapX="sm" gapY="2xs" justify="space-between">
                        <Text size="med" transform="uppercase">
                          {translations?.submissionForm?.header?.versionLabel}
                        </Text>
                        <Text font="OpenSans" size="sm">
                          {translations?.submissionForm?.header?.versionValue.replace(
                            '{version}',
                            String(island.version)
                          )}
                        </Text>
                      </Inline>
                      <Inline gapX="sm" gapY="2xs" justify="space-between">
                        <Text size="med" transform="uppercase">
                          {translations?.submissionForm?.header?.lastApprovedVersionLabel}
                        </Text>
                        <Text font="OpenSans" size="sm">
                          {island.lastApprovedVersion
                            ? translations?.submissionForm?.header?.lastApprovedVersionValue.replace(
                                '{version}',
                                String(island.lastApprovedVersion)
                              )
                            : translations.submissionForm.header.noLastApprovedVersionValue}
                        </Text>
                      </Inline>
                      <Inline gapX="sm" gapY="2xs" justify="space-between">
                        <Text size="med" transform="uppercase">
                          {translations?.submissionForm?.moderationStatus?.label}
                        </Text>
                        <Text font="OpenSans" size="sm">
                          {((status) => {
                            switch (status) {
                              case 'PENDING':
                                return translations?.submissionForm?.moderationStatus?.pending.replace(
                                  '{version}',
                                  String(island.submittedVersion?.version)
                                );
                              case 'APPROVED':
                                return translations?.submissionForm?.moderationStatus?.approved.replace(
                                  '{version}',
                                  String(island.submittedVersion?.version)
                                );
                              case 'DENIED':
                                return translations?.submissionForm?.moderationStatus?.rejected.replace(
                                  '{version}',
                                  String(island.submittedVersion?.version)
                                );
                              case 'UNMODERATED':
                                return translations?.submissionForm?.moderationStatus?.unmoderated;
                              default:
                                throw new Error(
                                  translations?.submissionForm?.moderationStatus?.error ??
                                    'Could not match moderation state'
                                );
                            }
                          })(island.moderationStatus)}
                        </Text>
                      </Inline>
                    </Stack>
                  </Stack>
                </Box>

                <Box size="2xl">
                  <Stack gap="2xl">
                    {hasExceededPendingLimit ? (
                      <Alert tone="critical">
                        {translations?.submissionForm?.statusAlertPending}
                      </Alert>
                    ) : null}
                    {island.isOutdated ? (
                      <Alert tone="critical">
                        {translations?.submissionForm?.statusAlertOutdated}
                      </Alert>
                    ) : null}
                    {hasBeenModerated ? (
                      <Alert tone="critical">
                        {translations.submissionForm.statusAlreadyModerated}
                      </Alert>
                    ) : null}
                    {isPendingModeration ? (
                      <Alert tone="critical">
                        {translations.submissionForm.statusAlertIslandPending}
                      </Alert>
                    ) : null}
                    <Stack gap="xl">
                      <Text color="#0B85EC" size="lg" transform="uppercase">
                        {translations?.submissionForm?.islandInformation?.title}
                      </Text>
                      <Stack gap="sm">
                        <Text size="med" transform="uppercase">
                          {translations?.submissionForm?.islandInformation?.descriptionLabel}
                        </Text>
                        <Text font="OpenSans" size="med">
                          {island.description}
                        </Text>
                      </Stack>
                      <Stack gap="sm">
                        <Text size="med" transform="uppercase">
                          {translations?.submissionForm?.islandInformation?.tagsLabel}
                        </Text>
                        <Inline gap="2xs">
                          {island.tags?.map((tag, index) => (
                            <StyledTag key={`${index}-${tag}`}>
                              <Text color="#5E5E5E" font="OpenSans" size="xs">
                                {tag}
                              </Text>
                            </StyledTag>
                          ))}
                        </Inline>
                      </Stack>
                      <Stack gap="sm">
                        <Text size="med" transform="uppercase">
                          {translations?.submissionForm?.islandInformation?.maxPlayerCountLabel}
                        </Text>
                        <Text font="OpenSans" size="med">
                          {island.maximumNumberOfPlayers}
                        </Text>
                      </Stack>
                      {island.image ? (
                        <Stack gap="sm">
                          <Text size="med" transform="uppercase">
                            {translations?.submissionForm?.islandInformation?.imageLabel}
                          </Text>
                          <img src={island.image} alt="" />
                        </Stack>
                      ) : null}
                    </Stack>
                  </Stack>
                </Box>
                <Form id={`submit-island-form`} onSubmit={handleSubmit}>
                  <Box size="2xl">
                    <Stack gap="xl">
                      <Text color="#0B85EC" size="lg" transform="uppercase">
                        {translations?.submissionForm?.mediaUpload?.title}
                      </Text>
                      <FormikFileUpload
                        name="image"
                        description={
                          translations?.submissionForm?.mediaUpload?.imageUploadDescription
                        }
                        tertiaryLabel={
                          translations?.submissionForm?.mediaUpload?.imageUploadTertiaryLabel
                        }
                        label={
                          translations?.submissionForm?.mediaUpload?.imageUploadLabel +
                          (island?.image === undefined ? '*' : '')
                        }
                        submissionIdentifier={submissionIdentifier.current}
                        disabled={!isSubmissionAllowed}
                        type="image"
                        translations={translations}
                      />
                      {errors.image ? <Alert tone="critical">{errors.image}</Alert> : null}
                      <FormikFileUpload
                        name="trailer"
                        description={
                          translations?.submissionForm?.mediaUpload?.videoUploadDescription
                        }
                        tertiaryLabel={
                          translations?.submissionForm?.mediaUpload?.videoUploadTertiaryLabel
                        }
                        label={translations?.submissionForm?.mediaUpload?.videoUploadLabel}
                        submissionIdentifier={submissionIdentifier.current}
                        disabled={!isSubmissionAllowed}
                        type="video"
                        translations={translations}
                      />
                      {errors.trailer ? <Alert tone="critical">{errors.trailer}</Alert> : null}
                    </Stack>
                  </Box>
                  <Box size="2xl">
                    <Stack gap="xl">
                      <Text color="#0B85EC" size="lg" transform="uppercase">
                        {translations?.submissionForm?.contactTitle}
                      </Text>
                      <Stack justify="center" gap="xl">
                        <TextField
                          message={translations?.submissionForm?.updateEmailString}
                          disabled={true}
                          id="email"
                          label={`${translations?.submissionForm?.emailLabel}*`}
                          readOnly={true}
                          value={email}
                        />
                      </Stack>
                    </Stack>
                  </Box>
                </Form>
              </FlexItem>
              <FlexItem>
                {isSubmitting && (
                  <Box backgroundColor="rgba(11, 133, 236, 0.16)">
                    <Stack gap="sm">
                      <Progress color={'#0B85EC'} value={progress} />
                      <Text align="center" font="OpenSans" size="xs">
                        Submitting Island to Discovery
                      </Text>
                    </Stack>
                  </Box>
                )}
                <Box backgroundColor="rgba(11, 133, 236, 0.16)" size="med">
                  <Stack gap="med">
                    {status?.message ? <Alert tone="critical">{status.message}</Alert> : null}
                    <Inline justify="space-between" gap="sm">
                      <Stack gap="sm">
                        <Text size="med" transform="uppercase">
                          {translations?.submissionForm?.footer?.title}
                        </Text>
                        <label htmlFor="acceptContentGuidelines">
                          <Inline align="center" gap="2xs">
                            <FormikField
                              id="acceptContentGuidelines"
                              name="acceptContentGuidelines"
                              type="checkbox"
                              style={{margin: 0}} /* undo global styles */
                              disabled={!isSubmissionAllowed}
                            />
                            <Text font="OpenSans" size="xs">
                              <LinkParagraph
                                linkDetails={translations?.submissionForm?.footer?.submissionLink}
                              />
                            </Text>
                          </Inline>
                        </label>
                        {errors.acceptContentGuidelines ? (
                          <Text font="OpenSans" size="xs" weight="500" color="red">
                            {errors.acceptContentGuidelines}
                          </Text>
                        ) : null}
                      </Stack>
                      <Inline gap="xs">
                        <UtilityButton
                          label={translations?.submissionForm?.footer?.cancelButtonString}
                          onClick={onDismiss}
                          theme="white"
                        />
                        <UtilityButton
                          disabled={!dirty || isSubmitting || !isValid || !isSubmissionAllowed}
                          label={translations?.submissionForm?.footer?.ctaButtonString}
                          onClick={submitForm}
                        />
                      </Inline>
                    </Inline>
                  </Stack>
                </Box>
              </FlexItem>
            </Flex>
          )}
        </Formik>
      ) : null}
    </StyledIslandFormModal>
  );
};
